import { useRef } from "react";
import { motion, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { useGo } from "./Header";
import { EASE } from "./Reveal";

const HeroFrame = ({ caption, progress }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotY = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 110, damping: 18 });
  const rotX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 110, damping: 18 });
  const sx = useTransform(mx, (v) => `${v * 100}%`);
  const sy = useTransform(my, (v) => `${v * 100}%`);
  const shade = useMotionTemplate`radial-gradient(380px circle at ${sx} ${sy}, rgba(10,11,13,0) 0%, rgba(10,11,13,0.82) 70%)`;
  const glow = useMotionTemplate`radial-gradient(260px circle at ${sx} ${sy}, rgba(255,85,0,0.28), transparent 70%)`;
  const imgY = useTransform(progress, [0, 1], ["-6%", "14%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { mx.set(0.5); my.set(0.5); };

  return (
    <motion.div
      initial={{ opacity: 0, clipPath: "inset(100% 0% 0% 0%)" }}
      animate={{ opacity: 1, clipPath: "inset(-10% -10% -10% -10%)" }}
      transition={{ duration: 1.5, delay: 0.35, ease: EASE }}
      className="relative lg:col-span-5"
      style={{ perspective: 1200 }}
    >
      <motion.div
        data-testid="hero-frame"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
        className="notch relative aspect-[5/4] w-full overflow-hidden bg-ink-2 lg:aspect-[4/5]"
      >
        <motion.img
          src="/img/hero.jpg"
          alt="Stage lit by dramatic beams of light before a live show"
          style={{ y: imgY }}
          className="absolute inset-x-0 -top-[10%] h-[125%] w-full object-cover grayscale contrast-125"
        />
        <motion.div aria-hidden className="absolute inset-0" style={{ background: shade }} />
        <motion.div aria-hidden className="absolute inset-0 mix-blend-screen" style={{ background: glow }} />
        <div aria-hidden className="absolute left-4 top-4 h-5 w-5 border-l border-t border-signal" />
        <div aria-hidden className="absolute bottom-4 right-4 h-5 w-5 border-b border-r border-signal" />
        <span className="absolute bottom-5 left-5 font-mono text-xs uppercase tracking-[0.2em] text-bone">TLN / 2026</span>
      </motion.div>
      <div className="mt-4 flex flex-wrap justify-between gap-2 font-mono text-xs text-zinc-400">
        <span>59.4370° N — 24.7536° E</span>
        <span>{caption}</span>
      </div>
    </motion.div>
  );
};

export const Hero = () => {
  const { t, lang } = useI18n();
  const go = useGo();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden bg-grid pb-14 pt-28 lg:pb-20 lg:pt-36">
      <div aria-hidden className="pointer-events-none absolute -left-40 -top-40 h-[620px] w-[620px] rounded-full bg-signal/10 blur-[150px]" />
      <div className="wrap relative grid items-end gap-12 lg:grid-cols-12 lg:gap-10">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: EASE }} className="eyebrow mb-8 flex items-center gap-3">
            <span className="h-px w-10 bg-signal" />{t.hero.eyebrow}
          </motion.p>
          <h1 key={lang} data-testid="hero-title" className="font-display text-[13vw] font-semibold leading-[0.9] tracking-[-0.045em] sm:text-[10vw] lg:text-[6.6vw] 2xl:text-[6.4rem]">
            {t.hero.lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-[0.07em]">
                <motion.span
                  className={`block ${i === t.hero.lines.length - 1 ? "text-signal" : ""}`}
                  initial={{ y: "108%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.15, delay: 0.15 + i * 0.12, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9, ease: EASE }} className="mt-10 max-w-xl">
            <p data-testid="hero-subtitle" className="text-base leading-relaxed text-zinc-300 sm:text-lg">{t.hero.sub}</p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button data-testid="hero-cta-contact" onClick={() => go("contact")} className="group flex items-center gap-3 rounded-full bg-signal px-7 py-4 font-medium text-ink transition-transform duration-300 hover:-translate-y-0.5 active:translate-y-0">
                {t.hero.cta}
                <span className="grid h-7 w-7 place-items-center rounded-full bg-ink text-bone transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
              </button>
              <button data-testid="hero-cta-services" onClick={() => go("services")} className="group flex items-center gap-2 px-2 py-4 text-bone">
                <span className="nav-link">{t.hero.cta2}</span>
                <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </button>
            </div>
          </motion.div>
        </motion.div>
        <HeroFrame caption={t.hero.caption} progress={scrollYProgress} />
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }} className="wrap relative mt-14 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
        <div className="flex flex-wrap gap-x-8 gap-y-2">{t.hero.meta.map((m) => <span key={m}>{m}</span>)}</div>
        <span className="flex items-center gap-3">{t.hero.scroll}<span className="relative h-8 w-px overflow-hidden bg-white/15"><motion.span className="absolute inset-x-0 top-0 h-3 bg-signal" animate={{ y: [-12, 32] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} /></span></span>
      </motion.div>
    </section>
  );
};
